/**
 * CreditMention — the shared way a `credit` renders wherever it's referenced
 * (the `creditLink` body annotation and the scene-credits sidebar).
 *
 * Rules (per product decision):
 *   • The annotation may pin a single `platform` → the name links to just that
 *     one (used by migrated inline links, e.g. an "OF" link stays OnlyFans).
 *   • Otherwise, if the credit has exactly ONE link → the name is that link.
 *   • If it has 2+ links → the name is plain text followed by a compact set of
 *     colored, brand-recognizable platform glyphs, each its own link:
 *         Kaysea  · IG · FL · Web ·
 *   • No links → plain text.
 *
 * Brand glyph path data (Instagram/OnlyFans/X/Patreon/TikTok) is from
 * simple-icons (CC0), inlined so there's no runtime dependency or external
 * request. Platforms without a brand mark fall back to a lucide icon (Website,
 * Store, Other) or a short text abbreviation (FetLife, submit.gg).
 */
import type { ReactNode } from 'react'
import { Globe, Store, ExternalLink } from 'lucide-react'

export interface CreditLink {
  platform: string
  url: string
  label?: string | null
}
export interface CreditData {
  _id?: string
  name?: string
  links?: CreditLink[] | null
}

// CC0 (simple-icons) 24x24 path data, inlined.
const IG =
  'M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077'
const OF =
  'M24 4.003h-4.015c-3.45 0-5.3.197-6.748 1.957a7.996 7.996 0 1 0 2.103 9.211c3.182-.231 5.39-2.134 6.085-5.173 0 0-2.399.585-4.43 0 4.018-.777 6.333-3.037 7.005-5.995zM5.61 11.999A2.391 2.391 0 0 1 9.28 9.97a2.966 2.966 0 0 1 2.998-2.528h.008c-.92 1.778-1.407 3.352-1.998 5.263A2.392 2.392 0 0 1 5.61 12Zm2.386-7.996a7.996 7.996 0 1 0 7.996 7.996 7.996 7.996 0 0 0-7.996-7.996Zm0 10.394A2.399 2.399 0 1 1 10.395 12a2.396 2.396 0 0 1-2.399 2.398Z'
const XI =
  'M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z'
const PATREON =
  'M22.957 7.21c-.004-3.064-2.391-5.576-5.191-6.482-3.478-1.125-8.064-.962-11.384.604C2.357 3.231 1.093 7.391 1.046 11.54c-.039 3.411.302 12.396 5.369 12.46 3.765.047 4.326-4.804 6.068-7.141 1.24-1.662 2.836-2.132 4.801-2.618 3.376-.836 5.678-3.501 5.673-7.031Z'
const TIKTOK =
  'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z'

interface PlatformMeta {
  label: string
  brandPath?: string
  brandColor?: string
  LucideIcon?: typeof Globe
  abbrev?: string // text fallback when there's no glyph
}
export const PLATFORM_META: Record<string, PlatformMeta> = {
  instagram: { label: 'Instagram', brandPath: IG, brandColor: '#E4405F' },
  onlyfans: { label: 'OnlyFans', brandPath: OF, brandColor: '#00AFF0' },
  x: { label: 'X (Twitter)', brandPath: XI, brandColor: 'currentColor' },
  patreon: { label: 'Patreon', brandPath: PATREON, brandColor: '#F96854' },
  tiktok: { label: 'TikTok', brandPath: TIKTOK, brandColor: 'currentColor' },
  website: { label: 'Website', LucideIcon: Globe },
  store: { label: 'Store', LucideIcon: Store },
  fetlife: { label: 'FetLife', abbrev: 'FL' },
  submitgg: { label: 'submit.gg', abbrev: 'submit.gg' },
  other: { label: 'Link', LucideIcon: ExternalLink },
}

/** Ordered platform values — used by the Studio schema list + the renderer. */
export const PLATFORM_OPTIONS = [
  'instagram', 'website', 'store', 'onlyfans', 'fetlife', 'x', 'submitgg', 'patreon', 'other',
] as const

function safeUrl(href: string | undefined | null): string | null {
  if (!href) return null
  const t = href.trim()
  if (/^\s*javascript:/i.test(t)) return null
  if (/^(https?:|mailto:|tel:)/i.test(t) || t.startsWith('/')) return t
  return null
}

function metaFor(platform: string): PlatformMeta {
  return PLATFORM_META[platform] || PLATFORM_META.other
}

/** A single colored brand glyph / lucide icon / text abbreviation. */
function PlatformGlyph({ platform, className }: { platform: string; className?: string }) {
  const m = metaFor(platform)
  if (m.brandPath) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden className={className} style={{ width: '1em', height: '1em', fill: m.brandColor || 'currentColor' }}>
        <path d={m.brandPath} />
      </svg>
    )
  }
  if (m.LucideIcon) {
    const Icon = m.LucideIcon
    return <Icon aria-hidden className={className} style={{ width: '1em', height: '1em' }} />
  }
  return <span className={className} style={{ fontSize: '0.8em', fontWeight: 600 }}>{m.abbrev || m.label}</span>
}

/**
 * Render a credit reference. `name` is the display text (the annotation's
 * wrapped text, or the credit's name in the scene sidebar). `platform` pins a
 * single link when set (annotation mode).
 */
export function CreditMention({
  name,
  credit,
  platform,
  linkClassName = 'text-brand-700 underline',
}: {
  name: ReactNode
  credit: CreditData | null | undefined
  platform?: string | null
  linkClassName?: string
}) {
  const links = (credit?.links || []).filter((l) => safeUrl(l.url))
  const nameText = credit?.name || (typeof name === 'string' ? name : 'credit')

  // Annotation pinned a platform → the name links to just that one.
  if (platform) {
    const chosen = links.find((l) => l.platform === platform) || links[0]
    const href = chosen && safeUrl(chosen.url)
    return href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>{name}</a>
    ) : (<>{name}</>)
  }

  if (links.length === 0) return <>{name}</>

  // Exactly one link → the name is the link.
  if (links.length === 1) {
    const href = safeUrl(links[0].url)!
    return <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>{name}</a>
  }

  // Multiple links → name + a compact row of platform glyphs, each a link.
  return (
    <span className="whitespace-nowrap">
      {name}{' '}
      <span className="inline-flex items-center gap-1.5 align-middle">
        {links.map((l, i) => {
          const href = safeUrl(l.url)!
          const m = metaFor(l.platform)
          return (
            <a
              key={`${l.platform}-${i}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${nameText} — ${l.label || m.label}`}
              title={l.label || m.label}
              className="inline-flex min-h-[24px] min-w-[24px] items-center justify-center text-brand-700 hover:opacity-70 transition-opacity"
              style={{ fontSize: '1.05em' }}
            >
              <PlatformGlyph platform={l.platform} />
            </a>
          )
        })}
      </span>
    </span>
  )
}
